import { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { CreditCheckStatus, IBlotterColumnDefinition, PlatformFilter } from 'state/blotters/common/blotter.model';
import { TradesBlottersItemByBlotterType } from 'state/blotters/trades/types/blotterItem';
import { GlobalFilters, TradeBlottersFiltersByBlotterType } from 'state/blotters/trades/types/filters';
import {
  BlotterType,
  FilterBlotterType,
  MasterDetailsBlotterType,
  PageBlotterType,
  PreferenceBlotterType,
} from 'state/blotters/trades/types/types';

export type TradesBlottersAction =
  | TradesBlottersAdvancedFiltersToggled
  | TradesBlottersColumnStateChanged
  | TradesBlottersSortChanged
  | TradesBlottersItemsFetchingFailed
  | TradesBlottersItemsPerPageChanged
  | TradesBlottersItemsReceived
  | TradesBlottersItemsReloaded
  | TradesBlottersItemsRequested
  | TradesBlotterManualRefreshRequested
  | TradesBlottersPreferenceStateGet
  | TradesBlottersPreferenceStateLoaded
  | TradesBlottersPreferenceStateLoadingFailed
  | TradesBlottersPreferenceStatePersisted
  | TradesBlottersCreditCheckStatusUpdated
  | TradesBlottersCreditCheckStatusRequest
  | TradesBlottersFiltersLoaded
  | TradesBlotterFiltersLoadingFailed
  | TradesBlottersGlobalFiltersLoaded
  | TradesBlottersGlobalFiltersLoadingFailed
  | TradesBlottersColumnDefinitionReceived
  | TradesBlottersFetchColumnDefinitionFailed
  | TradesBlottersFilterChanged
  | TradesBlotterFilterEnabled
  | TradeBlottersOpenDlpProduct
  | TradesBlotterPlatforms
  | TradesBlottersSetLoading;

export function tradesBlottersFiltersLoaded<T extends FilterBlotterType>(
  blotter: T,
  filters: TradeBlottersFiltersByBlotterType<T>,
) {
  return {
    type: 'TRADES_BLOTTERS_FILTERS_LOADED' as const,
    filters: filters,
    blotter: blotter,
  };
}

type TradesBlottersFiltersLoaded = ReturnType<typeof tradesBlottersFiltersLoaded>;

export function tradesBlottersGlobalFiltersLoaded(filters: GlobalFilters) {
  return {
    type: 'TRADES_BLOTTERS_GLOBAL_FILTERS_LOADED' as const,
    filters: filters,
  };
}

type TradesBlottersGlobalFiltersLoaded = ReturnType<typeof tradesBlottersGlobalFiltersLoaded>;

export function tradesBlottersGlobalFiltersLoadingFailed() {
  return {
    type: 'TRADES_BLOTTERS_GLOBAL_FILTERS_LOADING_FAILED' as const,
  };
}

type TradesBlottersGlobalFiltersLoadingFailed = ReturnType<typeof tradesBlottersGlobalFiltersLoadingFailed>;

export function tradesBlottersColumnDefinitionReceived<T extends BlotterType>(
  columnDefinition: IBlotterColumnDefinition<TradesBlottersItemByBlotterType<T>>[],
  blotter: T,
) {
  return {
    type: 'TRADES_BLOTTERS_COLUMN_DEFINITION_RECEIVED' as const,
    columnDefinition,
    blotter,
  };
}
export type TradesBlottersColumnDefinitionReceived = ReturnType<typeof tradesBlottersColumnDefinitionReceived>;

export function tradesBlottersFetchColumnDefinitionFailed<T extends BlotterType>(blotter: T) {
  return { type: 'TRADES_BLOTTERS_FETCH_COLUMN_DEFINITION_FAILED' as const, blotter };
}
export type TradesBlottersFetchColumnDefinitionFailed = ReturnType<typeof tradesBlottersFetchColumnDefinitionFailed>;

export function tradesBlottersItemsPerPageChanged<T extends PageBlotterType & PreferenceBlotterType>(
  blotter: T,
  itemsPerPage: number,
) {
  return {
    type: 'TRADES_BLOTTERS_ITEMS_PER_PAGE_CHANGED' as const,
    blotter,
    itemsPerPage,
  };
}

export type TradesBlottersItemsPerPageChanged = ReturnType<typeof tradesBlottersItemsPerPageChanged>;

export function tradeBlottersOpenDlpProduct(isin: string) {
  return {
    type: 'TRADES_BLOTTERS_OPEN_DLP_PRODUCT' as const,
    isin,
  };
}

export type TradeBlottersOpenDlpProduct = ReturnType<typeof tradeBlottersOpenDlpProduct>;

export function tradesBlottersFilterChanged<
  T extends FilterBlotterType,
  F extends keyof TradeBlottersFiltersByBlotterType<T> = keyof TradeBlottersFiltersByBlotterType<FilterBlotterType>,
>(blotter: T, filter: F, value: TradeBlottersFiltersByBlotterType<T>[F]) {
  return {
    type: 'TRADES_BLOTTERS_FILTER_CHANGED' as const,
    filter,
    value,
    blotter,
  };
}

export type TradesBlottersFilterChanged<
  T extends FilterBlotterType = FilterBlotterType,
  F extends keyof TradeBlottersFiltersByBlotterType<T> = keyof TradeBlottersFiltersByBlotterType<FilterBlotterType>,
> = ReturnType<typeof tradesBlottersFilterChanged<T, F>>;
export function tradesBlottersFilterEnabled<T extends FilterBlotterType>(blotter: T) {
  return {
    type: 'TRADES_BLOTTERS_FILTER_ENABLED' as const,
    blotter,
  };
}
export type TradesBlotterFilterEnabled = ReturnType<typeof tradesBlottersFilterEnabled>;

export function tradesBlottersAdvancedFiltersToggled<T extends FilterBlotterType>(blotter: T) {
  return {
    type: 'TRADES_BLOTTERS_ADVANCED_FILTERS_TOGGLED' as const,
    blotter,
  };
}
export type TradesBlottersAdvancedFiltersToggled = ReturnType<typeof tradesBlottersAdvancedFiltersToggled>;
export function tradesBlottersItemsReceived<T extends BlotterType>(
  blotter: T,
  items: readonly TradesBlottersItemByBlotterType<T>[],
  parentId: T extends MasterDetailsBlotterType ? string : undefined,
) {
  return {
    type: 'TRADES_BLOTTERS_ITEMS_RECEIVED' as const,
    blotter,
    items,
    parentId,
  };
}
export type TradesBlottersItemsReceived = ReturnType<typeof tradesBlottersItemsReceived>;

export function tradesBlottersItemsRequested<T extends BlotterType>(
  blotter: T,
  request: T extends 'dlpProductTrades' ? string : undefined,
) {
  return { type: 'TRADES_BLOTTERS_ITEMS_REQUESTED' as const, blotter, request };
}

export type TradesBlottersItemsRequested<T extends BlotterType = BlotterType> = ReturnType<
  typeof tradesBlottersItemsRequested<T>
>;

export function tradesBlottersItemsReloaded<T extends BlotterType>(blotter: T) {
  return { type: 'TRADES_BLOTTERS_ITEMS_RELOADED' as const, blotter };
}

export type TradesBlottersItemsReloaded = ReturnType<typeof tradesBlottersItemsReloaded>;

export function tradesBlottersSetLoading<T extends BlotterType>(
  blotter: T,
  isLoading: boolean,
  parentId: T extends MasterDetailsBlotterType ? string : undefined,
) {
  return { type: 'TRADES_BLOTTERS_SET_LOADING' as const, blotter, isLoading, parentId };
}

export type TradesBlottersSetLoading<T extends BlotterType = BlotterType> = ReturnType<
  typeof tradesBlottersSetLoading<T>
>;

export function tradesBlottersColumnStateChanged<T extends PreferenceBlotterType>(
  blotter: T,
  columnState: readonly any[],
) {
  return { type: 'TRADES_BLOTTERS_COLUMN_STATE_CHANGED' as const, blotter, columnState };
}

export type TradesBlottersColumnStateChanged = ReturnType<typeof tradesBlottersColumnStateChanged>;

export function tradesBlottersSortChanged(blotter: BlotterType, sort: string | null, sortDir: 'asc' | 'desc' | null) {
  return { type: 'TRADES_BLOTTERS_SORT_CHANGED' as const, blotter, sort, sortDir };
}

export type TradesBlottersSortChanged = ReturnType<typeof tradesBlottersSortChanged>;

export function tradesBlottersPreferenceStatePersisted<T extends PreferenceBlotterType>(
  blotterPreferenceState: IBlotterPreferenceState,
  blotter: T,
) {
  return { type: 'TRADES_BLOTTERS_PREFERENCE_STATE_PERSISTED' as const, blotterPreferenceState, blotter };
}

export type TradesBlottersPreferenceStatePersisted = ReturnType<typeof tradesBlottersPreferenceStatePersisted>;

export function tradesBlottersPreferenceStateLoaded<T extends PreferenceBlotterType>(
  blotter: T,
  blotterPreferenceState: IBlotterPreferenceState,
) {
  return { type: 'TRADES_BLOTTERS_PREFERENCE_STATE_LOADED' as const, blotter, blotterPreferenceState };
}
export type TradesBlottersPreferenceStateLoaded = ReturnType<typeof tradesBlottersPreferenceStateLoaded>;

export function tradesBlottersPreferenceStateLoadingFailed<T extends PreferenceBlotterType>(blotter: T) {
  return {
    type: 'TRADES_BLOTTERS_PREFERENCE_STATE_LOADING_FAILED' as const,
    blotter,
  };
}
export type TradesBlottersPreferenceStateLoadingFailed = ReturnType<typeof tradesBlottersPreferenceStateLoadingFailed>;

export function tradesBlottersPreferenceStateGet<T extends PreferenceBlotterType>(blotter: T) {
  return { type: 'TRADES_BLOTTERS_PREFERENCE_STATE_GET' as const, blotter };
}
export type TradesBlottersPreferenceStateGet = ReturnType<typeof tradesBlottersPreferenceStateGet>;

export function tradesBlottersItemsFetchingFailed<T extends BlotterType, E>(
  blotter: T,
  parentId: T extends MasterDetailsBlotterType ? string : undefined,
  errorContent?: E,
) {
  return { type: 'TRADES_BLOTTERS_ITEMS_FETCHING_FAILED' as const, blotter, error: errorContent, parentId };
}

type TradesBlottersItemsFetchingFailed = ReturnType<typeof tradesBlottersItemsFetchingFailed>;

export function tradesBlottersFiltersLoadingFailed<T extends FilterBlotterType>(blotter: T) {
  return { type: 'TRADES_BLOTTERS_FILTERS_LOADING_FAILED' as const, blotter };
}
type TradesBlotterFiltersLoadingFailed = ReturnType<typeof tradesBlottersFiltersLoadingFailed>;

export function tradesBlottersManualRefreshRequested<T extends BlotterType>(blotter: T) {
  return { type: 'TRADES_BLOTTERS_MANUAL_REFRESH_REQUESTED' as const, blotter };
}
type TradesBlotterManualRefreshRequested = ReturnType<typeof tradesBlottersManualRefreshRequested>;

export function tradesBlottersCreditCheckStatusUpdated(creditCheckStatus: CreditCheckStatus) {
  return { type: 'TRADES_BLOTTERS_CREDIT_CHECK_STATUS_UPDATED' as const, creditCheckStatus };
}
export type TradesBlottersCreditCheckStatusUpdated = ReturnType<typeof tradesBlottersCreditCheckStatusUpdated>;

export function tradesBlottersCreditCheckStatusRequested() {
  return { type: 'TRADES_BLOTTERS_CREDIT_CHECK_STATUS_REQUEST' as const };
}

export type TradesBlottersCreditCheckStatusRequest = ReturnType<typeof tradesBlottersCreditCheckStatusRequested>;

export function tradesBlottersPlatformsFetching() {
  return { type: 'TRADES_BLOTTER_PLATFORMS_FETCHING' as const };
}

type TradeBlottersPlatformsFetching = ReturnType<typeof tradesBlottersPlatformsFetching>;

export function tradesBlottersPlatformsFetched(data: ReadonlyArray<PlatformFilter>) {
  return { type: 'TRADES_BLOTTER_PLATFORMS_FETCHED' as const, data };
}

type TradesBlottersPlatformsFetched = ReturnType<typeof tradesBlottersPlatformsFetched>;
export function tradesBlottersPlatformsFetchedFailed(error: Error) {
  return { type: 'TRADES_BLOTTER_PLATFORMS_FETCH_FAILED' as const, error };
}
type TradesBlottersPlatformsFetchFailed = ReturnType<typeof tradesBlottersPlatformsFetchedFailed>;

type TradesBlotterPlatforms =
  | TradeBlottersPlatformsFetching
  | TradesBlottersPlatformsFetched
  | TradesBlottersPlatformsFetchFailed;

////////////////////////////////////////////////
